import React, { memo } from 'react';
import { EdgeProps, EdgeLabelRenderer } from 'reactflow';
import {
    usToMs,
    getLatencyColor,
    getBaselineDeviation,
    shortBandwidth,
    parseCarrier,
    formatLoss,
    getLossColor,
    formatMbps,
    getMetricColor,
    getUtilizationColor,
    getPacketLossOverlayColor,
    LinkLabelFields,
    DEFAULT_LINK_LABEL_FIELDS,
    AlgoLinkMode,
    flexAlgoLinkColor,
    TOPOLOGY_LINK_STROKE,
    getOfflineTiming,
} from '../TopologyData';

export interface LinkEdgeData {
    avgDelayUs: number;
    bandwidth: string;
    bandwidthShort?: string;
    isInterSite: boolean;
    baselineUs?: number | null;
    /** No directional telemetry but link was previously in baseline. */
    linkOffline?: boolean;
    /** Baseline `lastSeenAt` — last time the link was observed up. Drives the
     *  offline hover tooltip (last-seen-up + auto-removal countdown). */
    lastSeenUp?: string | null;
    forwardUs?: number | null;
    reverseUs?: number | null;
    forwardLoss?: number | null;
    reverseLoss?: number | null;
    forwardInboundMbps?: number | null;
    forwardOutboundMbps?: number | null;
    reverseInboundMbps?: number | null;
    reverseOutboundMbps?: number | null;
    isisMetric?: number | null;
    networkSharePct?: number | null;
    sourceDesc?: string | null;
    targetDesc?: string | null;
    onLabelClick?: () => void;
    linkFields?: LinkLabelFields;
    sptTreeEdge?: boolean;
    sptActive?: boolean;
    excluded?: boolean;
    trafficOverlay?: boolean;
    packetLossOverlay?: boolean;
    bandwidthKbps?: number;
    p2pPathEdge?: boolean;
    p2pActive?: boolean;
    /** Vertical pixel offset applied to the label so parallel edges don't stack. */
    labelOffset?: number;
    /** Flex-Algo filter — set when any algorithm is selected on the
     *  page. Even with `algoLinkMode === 'off'` we leave this `true`
     *  so the renderer knows to dim non-participating links if/when
     *  the user upgrades the mode. */
    algoFilterActive?: boolean;
    algoLinkMode?: AlgoLinkMode;
    /** Link is currently carrying real traffic for the selected algo
     *  (joined from `cef_srv6_locator_counters`). */
    algoTrafficLink?: boolean;
    /** Link is on the computed SPF path for the selected algo. */
    algoSpfLink?: boolean;
    /** Both endpoints have the selected flex-algo provisioned. */
    algoInParticipantSubgraph?: boolean;
}

const LinkEdge: React.FC<EdgeProps<LinkEdgeData>> = ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    data,
    selected,
}) => {
    const avgDelay = data?.avgDelayUs ?? 0;
    const bandwidth = data?.bandwidth ?? '';
    const bandwidthShort =
        data?.bandwidthShort ?? shortBandwidth(bandwidth);
    const isInterSite = data?.isInterSite ?? false;
    const baselineUs = data?.baselineUs ?? null;
    const linkOffline = data?.linkOffline ?? false;
    const offlineTiming = linkOffline
        ? getOfflineTiming(data?.lastSeenUp)
        : null;
    const offlineTooltip = offlineTiming
        ? `Offline — last seen up ${offlineTiming.lastSeenLabel} (${offlineTiming.lastSeenAgo}); auto-removes ${offlineTiming.removesInLabel}`
        : linkOffline
        ? 'Offline — no directional telemetry'
        : undefined;
    const forwardUs = data?.forwardUs ?? null;
    const reverseUs = data?.reverseUs ?? null;
    const onLabelClick = data?.onLabelClick;
    const fields: LinkLabelFields =
        data?.linkFields ?? DEFAULT_LINK_LABEL_FIELDS;
    const forwardLoss = data?.forwardLoss ?? null;
    const reverseLoss = data?.reverseLoss ?? null;
    const maxLoss =
        forwardLoss != null && reverseLoss != null
            ? Math.max(forwardLoss, reverseLoss)
            : forwardLoss ?? reverseLoss;
    const carrier =
        parseCarrier(data?.sourceDesc) ?? parseCarrier(data?.targetDesc);

    const fwdIn = data?.forwardInboundMbps ?? null;
    const fwdOut = data?.forwardOutboundMbps ?? null;
    const revIn = data?.reverseInboundMbps ?? null;
    const revOut = data?.reverseOutboundMbps ?? null;
    const rxMbps =
        fwdIn != null || revIn != null
            ? Math.max(fwdIn ?? 0, revIn ?? 0)
            : null;
    const txMbps =
        fwdOut != null || revOut != null
            ? Math.max(fwdOut ?? 0, revOut ?? 0)
            : null;
    const hasBw = rxMbps != null || txMbps != null;
    const isisMetric = data?.isisMetric ?? null;
    const networkSharePct = data?.networkSharePct ?? null;

    const pingUs =
        forwardUs != null && reverseUs != null
            ? forwardUs + reverseUs
            : avgDelay * 2;

    /** Health color (latency vs baseline). Used to colour the line itself. */
    const color = linkOffline
        ? TOPOLOGY_LINK_STROKE.offline
        : getLatencyColor(avgDelay, baselineUs);
    const strokeWidth = isInterSite ? 1.8 : 2;

    const deviation =
        !linkOffline && baselineUs != null && baselineUs > 0
            ? getBaselineDeviation(avgDelay, baselineUs)
            : null;

    /**
     * Edge geometry.
     *
     * Inter-site links are drawn as smooth quadratic Bezier curves. The
     * control point is offset perpendicular to the source→target line by
     * `labelOffset` (which TopologyGraph also uses to spread parallel
     * edges), so multiple parallels between the same pair of sites fan
     * apart instead of stacking on top of each other. Single inter-site
     * edges get a small default curvature so the path reads as an arc.
     *
     * Intra-site links stay straight: they connect adjacent router cards
     * inside the same site, and we already use a lifted-label + dashed
     * leader pattern when the line crosses an intermediate card.
     *
     * Both kinds of paths are slightly shrunk at the endpoints so the
     * line tail sits just outside each card rather than touching its border.
     */
    const dxRaw = targetX - sourceX;
    const dyRaw = targetY - sourceY;
    const len = Math.max(1, Math.hypot(dxRaw, dyRaw));
    const SHRINK = 6;
    const ux = dxRaw / len;
    const uy = dyRaw / len;
    const sx = sourceX + ux * SHRINK;
    const sy = sourceY + uy * SHRINK;
    const tx = targetX - ux * SHRINK;
    const ty = targetY - uy * SHRINK;

    const labelYOffset = data?.labelOffset ?? 0;
    /** Perpendicular unit vector (rotate the unit direction 90°). */
    const perpX = -uy;
    const perpY = ux;
    /** How far the control point is offset from the straight midline. */
    const DEFAULT_CURVE = 50;
    const curveOffset = isInterSite
        ? labelYOffset !== 0
            ? labelYOffset
            : DEFAULT_CURVE
        : 0;
    const straightMidX = (sourceX + targetX) / 2;
    const straightMidY = (sourceY + targetY) / 2;
    const controlX = straightMidX + perpX * curveOffset;
    const controlY = straightMidY + perpY * curveOffset;

    const edgePath =
        curveOffset === 0
            ? `M ${sx},${sy} L ${tx},${ty}`
            : `M ${sx},${sy} Q ${controlX},${controlY} ${tx},${ty}`;

    /** Point on the actual rendered path at t=0.5 — used as the label/leader anchor. */
    const pathMidX =
        curveOffset === 0
            ? straightMidX
            : (sourceX + 2 * controlX + targetX) / 4;
    const pathMidY =
        curveOffset === 0
            ? straightMidY
            : (sourceY + 2 * controlY + targetY) / 4;

    /**
     * Intra-site label lift logic.
     *
     *  - Adjacent routers (short horizontal span): the line fits inside the
     *    gap between two cards, so we keep the label inline at the midpoint.
     *  - Skipping an intermediate card (long horizontal span): the line
     *    passes over a sibling card, so we lift the label above the row and
     *    draw a dashed leader from the label down to the line midpoint.
     */
    const horizontalSpan = Math.abs(targetX - sourceX);
    const INTRA_LIFT_THRESHOLD = 150;
    const INTRA_LIFT = 80;
    const needsLift = !isInterSite && horizontalSpan > INTRA_LIFT_THRESHOLD;
    const intraLift = needsLift ? -INTRA_LIFT : 0;
    const labelPosX = pathMidX;
    const labelPosY = pathMidY + intraLift;
    /** Vertical distance the leader spans (label bottom → line midpoint). */
    const leaderHeight = needsLift
        ? Math.max(0, pathMidY - (labelPosY + 14))
        : 0;

    // ── SPT / P2P / Traffic overlay state ──
    const sptTreeEdge = data?.sptTreeEdge ?? false;
    const sptActive = data?.sptActive ?? false;
    const isExcluded = data?.excluded ?? false;
    const trafficOverlay = data?.trafficOverlay ?? false;
    const packetLossOverlay = data?.packetLossOverlay ?? false;
    const bwKbps = data?.bandwidthKbps ?? 0;
    const p2pPathEdge = data?.p2pPathEdge ?? false;
    const p2pActive = data?.p2pActive ?? false;

    // ── Flex-Algo state ──
    // `algoActive` here means "the dropdown has an algo picked AND the
    // user picked a link-level mode" — i.e. we should colour links.
    // When `algoLinkMode === 'off'` the link layer stays untouched so
    // only the node-level dimming (RouterNode) applies.
    const algoFilterActive = data?.algoFilterActive ?? false;
    const algoLinkMode = data?.algoLinkMode ?? 'off';
    const algoTrafficLink = data?.algoTrafficLink ?? false;
    const algoSpfLink = data?.algoSpfLink ?? false;
    const algoInParticipantSubgraph = data?.algoInParticipantSubgraph ?? true;
    const algoLinkActive =
        algoFilterActive && algoLinkMode !== 'off';
    const algoIsHighlighted =
        (algoLinkMode === 'traffic' && algoTrafficLink) ||
        (algoLinkMode === 'spf' && algoSpfLink) ||
        (algoLinkMode === 'both' &&
            (algoTrafficLink || algoSpfLink));

    const peakMbps = Math.max(rxMbps ?? 0, txMbps ?? 0);
    const capacityMbps = bwKbps > 0 ? bwKbps / 1000 : 0;
    const utilizationPct =
        capacityMbps > 0 ? (peakMbps / capacityMbps) * 100 : null;

    const SPT_COLOR = 'var(--color-semantic-text-brand, #5ce5d5)';
    const P2P_COLOR = 'var(--color-utility-brand-fuchsia-500, #ff4089)';
    const SELECTED_COLOR = 'var(--color-semantic-fg-brand-primary)';
    /** Flex-Algo: green = live traffic; brand = SPF-only in `both` mode. */
    const algoColor = flexAlgoLinkColor(
        algoLinkMode,
        algoTrafficLink,
        algoSpfLink,
        algoIsHighlighted
    );

    /** Packet-loss overlay: 3-tier severity driven by the worse of the two
     *  directions (`maxLoss`). Thresholds live in TopologyData so the legend
     *  swatches and the edge colour share one source of truth.
     *
     *  Priority order from top to bottom: P2P > packet-loss/traffic
     *  overlays > SPT > Flex-Algo > default latency colour. P2P
     *  always wins because it's the most direct gesture; Flex-Algo
     *  sits below SPT so that both can run together when needed. */
    const baseColor = p2pActive
        ? p2pPathEdge
            ? P2P_COLOR
            : 'var(--color-semantic-text-secondary)'
        : packetLossOverlay
        ? getPacketLossOverlayColor(maxLoss)
        : trafficOverlay
        ? getUtilizationColor(utilizationPct)
        : sptActive
        ? sptTreeEdge
            ? SPT_COLOR
            : 'var(--color-semantic-text-secondary)'
        : algoLinkActive
        ? algoIsHighlighted
            ? algoColor
            : 'var(--color-semantic-text-secondary)'
        : color;
    const activeColor = selected ? SELECTED_COLOR : baseColor;
    const activeStroke = selected
        ? strokeWidth + 1
        : p2pPathEdge
        ? strokeWidth + 1.5
        : sptTreeEdge
        ? strokeWidth + 1
        : algoLinkActive && algoIsHighlighted
        ? strokeWidth + 1
        : strokeWidth;
    const activeOpacity = isExcluded
        ? 0.15
        : selected
        ? 1
        : p2pActive && !p2pPathEdge
        ? 0.12
        : sptActive && !sptTreeEdge
        ? 0.2
        : algoLinkActive && algoIsHighlighted
        ? 1
        : algoLinkActive && !algoIsHighlighted
        ? algoInParticipantSubgraph
            ? 0.55
            : 0.3
        : 1;
    /**
     * Flex-algo dash pattern:
     *  - traffic (solid green)     → no dash
     *  - SPF-only in both (brand)  → dashed
     *  - SPF-only in spf mode      → dashed green
     *  - not on best path          → dashed grey
     */
    const algoDash = ((): string | undefined => {
        if (!algoLinkActive) return undefined;
        if (algoIsHighlighted) {
            const solidTraffic =
                algoTrafficLink &&
                (algoLinkMode === 'traffic' || algoLinkMode === 'both');
            return solidTraffic ? 'none' : '8 4';
        }
        return '6 4';
    })();

    return (
        <>
            {/* Glow under selected / P2P paths */}
            {(selected || p2pPathEdge) && (
                <path
                    d={edgePath}
                    style={{
                        stroke:
                            p2pPathEdge && !selected
                                ? P2P_COLOR
                                : SELECTED_COLOR,
                        strokeWidth: activeStroke + 6,
                        opacity: p2pPathEdge && !selected ? 0.18 : 0.3,
                        fill: 'none',
                        strokeDasharray: 'none',
                        pointerEvents: 'none',
                    }}
                />
            )}
            {/* Soft glow under flex-algo highlighted edges — traffic (green)
                and SPF (brand in `both`, green in spf-only). Rendered
                beneath the main stroke so the halo reads as depth. */}
            {algoLinkActive &&
                algoIsHighlighted &&
                !selected &&
                !p2pPathEdge && (
                    <path
                        d={edgePath}
                        style={{
                            stroke: algoColor,
                            strokeWidth: activeStroke + 6,
                            opacity: algoTrafficLink ? 0.2 : 0.15,
                            fill: 'none',
                            strokeLinecap: 'round',
                            pointerEvents: 'none',
                        }}
                    />
                )}
            <path
                id={id}
                className="react-flow__edge-path"
                d={edgePath}
                style={{
                    stroke: activeColor,
                    strokeWidth: activeStroke,
                    strokeDasharray: algoDash ?? '6 4',
                    strokeLinecap: 'round',
                    opacity: activeOpacity,
                    fill: 'none',
                    transition:
                        'stroke 0.2s, stroke-width 0.2s, opacity 0.2s',
                }}
            />
            {/* Wider, invisible interactor on top so clicks land on the curve
                even when several arcs run close together. */}
            <path
                d={edgePath}
                fill="none"
                stroke="transparent"
                strokeWidth={Math.max(14, activeStroke + 12)}
                style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
            >
                {offlineTooltip && <title>{offlineTooltip}</title>}
            </path>
            <EdgeLabelRenderer>
                {/* When the edge is selected (or on a P2P path), draw a copy
                    of the line above the cards so its endpoints are clearly
                    visible end-to-end. Cards normally cover the line; this
                    overlay lives in the EdgeLabelRenderer (z-index 5). */}
                {(selected || p2pPathEdge) && (
                    <svg
                        className="nodrag nopan"
                        width="100%"
                        height="100%"
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: '100%',
                            height: '100%',
                            overflow: 'visible',
                            pointerEvents: 'none',
                            zIndex: 49,
                        }}
                    >
                        {/* Soft halo behind the selected line for emphasis. */}
                        <path
                            d={edgePath}
                            stroke={
                                p2pPathEdge && !selected
                                    ? P2P_COLOR
                                    : SELECTED_COLOR
                            }
                            strokeWidth={activeStroke + 8}
                            strokeLinecap="round"
                            fill="none"
                            opacity={0.18}
                        />
                        {/* The duplicated, clearly readable selected line. */}
                        <path
                            d={edgePath}
                            stroke={
                                p2pPathEdge && !selected
                                    ? P2P_COLOR
                                    : SELECTED_COLOR
                            }
                            strokeWidth={activeStroke + 1.5}
                            strokeDasharray="6 4"
                            strokeLinecap="round"
                            fill="none"
                        />
                        {/* Endpoint anchors — outer halo + filled dot + ring —
                            so it's unambiguous which two cards the line
                            connects. */}
                        <circle
                            cx={sourceX}
                            cy={sourceY}
                            r={10}
                            fill={
                                p2pPathEdge && !selected
                                    ? P2P_COLOR
                                    : SELECTED_COLOR
                            }
                            opacity={0.18}
                        />
                        <circle
                            cx={sourceX}
                            cy={sourceY}
                            r={5}
                            fill={
                                p2pPathEdge && !selected
                                    ? P2P_COLOR
                                    : SELECTED_COLOR
                            }
                            stroke="white"
                            strokeWidth={2}
                        />
                        <circle
                            cx={targetX}
                            cy={targetY}
                            r={10}
                            fill={
                                p2pPathEdge && !selected
                                    ? P2P_COLOR
                                    : SELECTED_COLOR
                            }
                            opacity={0.18}
                        />
                        <circle
                            cx={targetX}
                            cy={targetY}
                            r={5}
                            fill={
                                p2pPathEdge && !selected
                                    ? P2P_COLOR
                                    : SELECTED_COLOR
                            }
                            stroke="white"
                            strokeWidth={2}
                        />
                    </svg>
                )}
                {/* Leader line: dashed connector from a lifted intra-site
                    label down to the actual edge midpoint between cards. */}
                {needsLift && leaderHeight > 0 && (
                    <div
                        className="nodrag nopan"
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, 0) translate(${pathMidX}px,${labelPosY + 14}px)`,
                            width: 1,
                            height: leaderHeight,
                            backgroundImage:
                                'linear-gradient(to bottom, var(--color-semantic-text-secondary) 50%, transparent 50%)',
                            backgroundSize: '1px 4px',
                            backgroundRepeat: 'repeat-y',
                            opacity: 0.5,
                            pointerEvents: 'none',
                            zIndex: selected ? 39 : 14,
                        }}
                    />
                )}
                <div
                    className="nodrag nopan pointer-events-auto cursor-pointer"
                    style={{
                        position: 'absolute',
                        transform: `translate(-50%, -50%) translate(${labelPosX}px,${labelPosY}px)`,
                        zIndex: selected ? 40 : 15,
                    }}
                    onClick={onLabelClick}
                >
                    <div
                        className={`flex flex-col items-center
                            rounded-md border bg-semantic-bg-primary
                            ${
                                selected
                                    ? 'shadow-md px-2 py-1'
                                    : 'shadow-sm px-1.5 py-0.5'
                            }`}
                        style={{
                            borderColor: selected
                                ? 'var(--color-semantic-fg-brand-primary)'
                                : 'var(--color-semantic-border-secondary)',
                            transition: 'all 0.18s ease',
                        }}
                    >
                        {fields.latency && (
                            <span
                                style={{
                                    fontSize: selected ? 12 : 10,
                                    fontWeight: 700,
                                    color: 'var(--color-semantic-text-primary)',
                                    lineHeight: 1.2,
                                    letterSpacing: '-0.2px',
                                    fontFamily:
                                        'ui-monospace, SFMono-Regular, Menlo, monospace',
                                }}
                            >
                                {linkOffline ? 'Offline' : usToMs(avgDelay)}
                            </span>
                        )}
                        {fields.rtt && !linkOffline && (
                            <span
                                style={{
                                    fontSize: selected ? 10 : 8,
                                    fontWeight: 500,
                                    color:
                                        'var(--color-semantic-text-secondary)',
                                    lineHeight: 1.2,
                                    fontFamily:
                                        'ui-monospace, SFMono-Regular, Menlo, monospace',
                                }}
                            >
                                RTT {usToMs(pingUs)}
                            </span>
                        )}
                        {selected && (
                            <div className="w-full h-px bg-semantic-border-primary my-0.5" />
                        )}
                        {fields.baseline && deviation && (
                            <span
                                className="font-semibold leading-tight"
                                style={{
                                    fontSize: selected ? 10 : 8,
                                    color: activeColor,
                                }}
                            >
                                {deviation}
                            </span>
                        )}
                        {fields.isisMetric && isisMetric != null && (
                            <span
                                style={{
                                    fontSize: selected ? 10 : 8,
                                    fontWeight: 600,
                                    color: getMetricColor(isisMetric),
                                    lineHeight: 1.2,
                                }}
                            >
                                Cost {isisMetric}
                            </span>
                        )}
                        {fields.bandwidth && (
                            <span
                                style={{
                                    fontSize: selected ? 10 : 8,
                                    color:
                                        'var(--color-semantic-text-secondary)',
                                    lineHeight: 1.2,
                                    fontWeight: 500,
                                    fontFamily:
                                        'ui-monospace, SFMono-Regular, Menlo, monospace',
                                }}
                            >
                                {bandwidthShort}
                            </span>
                        )}
                        {fields.carrier && carrier && (
                            <span
                                style={{
                                    fontSize: selected ? 9 : 7,
                                    fontWeight: 600,
                                    color: 'var(--color-semantic-text-brand)',
                                    lineHeight: 1.2,
                                    letterSpacing: '0.3px',
                                }}
                            >
                                {carrier}
                            </span>
                        )}
                        {fields.throughput && hasBw && (
                            <>
                                <span
                                    style={{
                                        fontSize: selected ? 9 : 7,
                                        lineHeight: 1.2,
                                        color:
                                            'var(--color-semantic-text-secondary)',
                                    }}
                                >
                                    <span style={{ fontWeight: 600 }}>Rx</span>{' '}
                                    {formatMbps(rxMbps)}
                                </span>
                                <span
                                    style={{
                                        fontSize: selected ? 9 : 7,
                                        lineHeight: 1.2,
                                        color:
                                            'var(--color-semantic-text-secondary)',
                                    }}
                                >
                                    <span style={{ fontWeight: 600 }}>Tx</span>{' '}
                                    {formatMbps(txMbps)}
                                </span>
                            </>
                        )}
                        {fields.utilization && networkSharePct != null && (
                            <span
                                style={{
                                    fontSize: selected ? 9 : 7,
                                    fontWeight: 600,
                                    color:
                                        'var(--color-semantic-text-secondary)',
                                    lineHeight: 1.2,
                                }}
                            >
                                {networkSharePct < 0.1
                                    ? '<0.1'
                                    : networkSharePct.toFixed(1)}
                                % BW
                            </span>
                        )}
                        {fields.loss && maxLoss != null && (
                            <span
                                style={{
                                    fontSize: selected ? 9 : 7,
                                    fontWeight: 700,
                                    color:
                                        maxLoss > 0
                                            ? getLossColor(maxLoss)
                                            : 'var(--color-semantic-text-secondary)',
                                    lineHeight: 1.2,
                                }}
                            >
                                Loss {formatLoss(maxLoss)}
                            </span>
                        )}
                    </div>
                </div>
            </EdgeLabelRenderer>
        </>
    );
};

export default memo(LinkEdge);
